import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Chip, Divider, Tabs, Tab, CircularProgress,
} from '@mui/material';
import { SportsScore, Warning, BarChart, Edit } from '@mui/icons-material';
import axios from 'axios';
import { BASE_URL, apiCall } from '../api/apiClient';
import StatisticsTab from './gameTabs/StatisticsTab';
import GoalTab from './gameTabs/GoalTab';
import PenaltiesTab from './gameTabs/PenaltiesTab';
import ChangesTab from './gameTabs/ChangesTab';
import FaultTab from './gameTabs/FaultTab';
import PlayersTab from './gameTabs/PlayersTab';

const statusColor = {
  live: 'error',
  scheduled: 'info',
  completed: 'success',
  postponed: 'warning',
  cancelled: 'default',
};

const UPDATE_TABS = [
  { label: 'Goals',     Component: GoalTab },
  { label: 'Penalties', Component: PenaltiesTab },
  { label: 'Changes',   Component: ChangesTab },
  { label: 'Faults',    Component: FaultTab },
  { label: 'Players',   Component: PlayersTab },
];

// Shared style tokens
const BG        = '#ffffff';
const SURFACE   = '#f6f8f7';
const BORDER    = '#e2e8e4';
const ACCENT    = '#16a34a';       // green-600
const ACCENT_BG = '#f0fdf4';      // very faint green tint
const TEXT_PRI  = '#0f1f14';      // near-black
const TEXT_SEC  = '#4b6358';      // muted green-grey

const GameViewDialog = ({ open, onClose, game: initialGame, onRefresh }) => {
  const [game, setGame]           = useState(initialGame);
  const [mainTab, setMainTab]     = useState(0);
  const [updateTab, setUpdateTab] = useState(0);
  const [fetching, setFetching]   = useState(false);

  const fetchGame = useCallback(async (id) => {
    if (!id) return;
    setFetching(true);
    try {
      const res = await apiCall((headers) =>
        axios.get(`${BASE_URL}/games/${id}`, { headers })
      );
      setGame(res.data.data);
      onRefresh();
    } catch (_) {}
    finally { setFetching(false); }
  }, [onRefresh]);

  useEffect(() => {
    if (open && initialGame) setGame(initialGame);
  }, [open, initialGame]);

  if (!game) return null;
  const e1 = game.entity1;
  const e2 = game.entity2;
  const { Component: ActiveUpdateTab } = UPDATE_TABS[updateTab];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          minHeight: '70vh',
          bgcolor: BG,
          color: TEXT_PRI,
          boxShadow: '0 24px 64px rgba(0,0,0,0.12)',
          overflow: 'hidden',
        },
      }}
    >
      {/* ── Header ── */}
      <DialogTitle sx={{ bgcolor: BG, px: 3, pt: 2.5, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 36, height: 36, borderRadius: '10px',
              bgcolor: ACCENT_BG, color: ACCENT,
            }}>
              <SportsScore fontSize="small" />
            </Box>
            <Typography variant="h6" fontWeight={700} color={TEXT_PRI} letterSpacing="-0.3px">
              {e1?.name} <Box component="span" sx={{ color: TEXT_SEC, fontWeight: 400 }}>vs</Box> {e2?.name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {fetching && <CircularProgress size={14} sx={{ color: ACCENT }} />}
            <Chip
              label={game.status}
              color={statusColor[game.status] || 'default'}
              size="small"
              sx={{ fontWeight: 600, fontSize: 11, textTransform: 'capitalize', borderRadius: '6px' }}
            />
          </Box>
        </Box>
      </DialogTitle>

      <Divider sx={{ borderColor: BORDER }} />

      {/* ── Score banner ── */}
      <Box sx={{
        textAlign: 'center',
        py: 3.5,
        px: 2,
        bgcolor: SURFACE,
        borderBottom: `1px solid ${BORDER}`,
        position: 'relative',
      }}>
        {/* subtle decorative line */}
        <Box sx={{
          position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
          width: 48, height: 3, borderRadius: '0 0 4px 4px',
          bgcolor: ACCENT,
        }} />

        <Typography
          variant="h2"
          fontWeight={800}
          color={TEXT_PRI}
          letterSpacing="-1px"
          sx={{ fontFeatureSettings: '"tnum"', lineHeight: 1 }}
        >
          {game.score?.entity1}
          <Box component="span" sx={{ mx: 2, color: TEXT_SEC, fontWeight: 300 }}>—</Box>
          {game.score?.entity2}
        </Typography>

        <Typography variant="body2" color={TEXT_SEC} sx={{ mt: 1, fontWeight: 500 }}>
          {e1?.name} &nbsp;·&nbsp; {e2?.name}
        </Typography>

        {(game.tempAdditionnel > 0 || game.gameInterrupted) && (
          <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
            {game.tempAdditionnel > 0 && (
              <Chip
                label={`+${game.tempAdditionnel} min`}
                size="small"
                color="warning"
                sx={{ fontWeight: 600, borderRadius: '6px', fontSize: 12 }}
              />
            )}
            {game.gameInterrupted && (
              <Chip
                icon={<Warning sx={{ fontSize: '14px !important' }} />}
                label={`Interrompu : ${game.raisonOfInterruption}`}
                size="small"
                color="error"
                sx={{ fontWeight: 600, borderRadius: '6px', fontSize: 12 }}
              />
            )}
          </Box>
        )}
      </Box>

      {/* ── Main tabs ── */}
      <Box sx={{ borderBottom: `1px solid ${BORDER}`, px: 3, bgcolor: BG }}>
        <Tabs
          value={mainTab}
          onChange={(_, v) => setMainTab(v)}
          sx={{
            '& .MuiTab-root': {
              color: TEXT_SEC,
              fontWeight: 600,
              fontSize: 13,
              textTransform: 'none',
              minHeight: 44,
              px: 1.5,
              mr: 1,
            },
            '& .Mui-selected': { color: `${ACCENT} !important` },
            '& .MuiTabs-indicator': { bgcolor: ACCENT, height: 2, borderRadius: 2 },
          }}
        >
          <Tab icon={<BarChart sx={{ fontSize: 17 }} />} iconPosition="start" label="Statistiques" />
          <Tab icon={<Edit sx={{ fontSize: 17 }} />} iconPosition="start" label="Modifier" />
        </Tabs>
      </Box>

      {/* ── Content ── */}
      <DialogContent sx={{ pt: 2.5, px: 3, bgcolor: BG, color: TEXT_PRI }}>
        {mainTab === 0 && <StatisticsTab game={game} />}
        {mainTab === 1 && (
          <Box>
            <Tabs
              value={updateTab}
              onChange={(_, v) => setUpdateTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                mb: 2.5,
                borderBottom: `1px solid ${BORDER}`,
                '& .MuiTab-root': {
                  color: TEXT_SEC,
                  fontWeight: 600,
                  fontSize: 12,
                  textTransform: 'none',
                  minHeight: 40,
                  minWidth: 80,
                  px: 1.5,
                  borderRadius: '8px 8px 0 0',
                  transition: 'background 0.15s',
                  '&:hover': { bgcolor: ACCENT_BG },
                },
                '& .Mui-selected': { color: `${ACCENT} !important`, bgcolor: ACCENT_BG },
                '& .MuiTabs-indicator': { bgcolor: ACCENT, height: 2, borderRadius: 2 },
              }}
            >
              {UPDATE_TABS.map(({ label }) => (
                <Tab key={label} label={label} />
              ))}
            </Tabs>
            <ActiveUpdateTab game={game} onRefresh={() => fetchGame(game._id)} />
          </Box>
        )}
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, bgcolor: BG, borderTop: `1px solid ${BORDER}` }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: BORDER,
            color: TEXT_PRI,
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: '10px',
            px: 3,
            '&:hover': { borderColor: ACCENT, color: ACCENT, bgcolor: ACCENT_BG },
          }}
        >
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GameViewDialog;